import { Flex, Input } from '@chakra-ui/react'

import { FormField } from '../../../components/FormField'
import { MaskedInput, useZipCodeLookup } from '../../../components/MaskedInput'
import { ITestimonialVideoAddress } from '../../../services/TestimonialVideo/types'

interface IAddressFieldsProps {
  value: ITestimonialVideoAddress
  onChange: (patch: Partial<ITestimonialVideoAddress>) => void
  /** First error message of a field, by its API key. */
  error: (key: string) => string | undefined
  isDisabled?: boolean
}

/** Address block (CEP lookup + manual fields) shared by the public form and the edit modal. */
export default function AddressFields({ value, onChange, error, isDisabled }: IAddressFieldsProps) {
  const { loadingZipCode, lookup } = useZipCodeLookup({ onAddressFound: (address) => onChange(address) })

  const handleZipCode = (zip_code: string) => {
    onChange({ zip_code })
    lookup(zip_code)
  }

  return (
    <>
      <Flex gap={4} direction={{ base: 'column', md: 'row' }} mt={4}>
        <FormField label="CEP" error={error('zip_code')} isRequired w={{ base: '100%', md: '180px' }} flexShrink={0}>
          <MaskedInput
            mask="zipCode"
            value={value.zip_code}
            onChange={handleZipCode}
            isDisabled={isDisabled || loadingZipCode}
          />
        </FormField>
        <FormField label="Logradouro" error={error('street')} isRequired flex={1}>
          <Input
            value={value.street}
            onChange={(e) => onChange({ street: e.target.value })}
            isDisabled={isDisabled}
            isReadOnly={loadingZipCode}
            autoComplete="address-line1"
          />
        </FormField>
      </Flex>

      <Flex gap={4} direction={{ base: 'column', md: 'row' }} mt={4}>
        <FormField label="Número" error={error('number')} isRequired w={{ base: '100%', md: '180px' }} flexShrink={0}>
          <Input
            value={value.number}
            onChange={(e) => onChange({ number: e.target.value })}
            isDisabled={isDisabled}
            maxLength={20}
          />
        </FormField>
        <FormField label="Complemento" error={error('complement')} flex={1}>
          <Input
            value={value.complement}
            onChange={(e) => onChange({ complement: e.target.value })}
            isDisabled={isDisabled}
            autoComplete="address-line2"
          />
        </FormField>
      </Flex>

      <Flex gap={4} direction={{ base: 'column', md: 'row' }} mt={4}>
        <FormField label="Bairro" error={error('neighborhood')} isRequired flex={1}>
          <Input
            value={value.neighborhood}
            onChange={(e) => onChange({ neighborhood: e.target.value })}
            isDisabled={isDisabled}
            isReadOnly={loadingZipCode}
          />
        </FormField>
        <FormField label="Cidade" error={error('city')} isRequired flex={1}>
          <Input
            value={value.city}
            onChange={(e) => onChange({ city: e.target.value })}
            isDisabled={isDisabled}
            isReadOnly={loadingZipCode}
            autoComplete="address-level2"
          />
        </FormField>
        <FormField label="UF" error={error('state')} isRequired w={{ base: '100%', md: '90px' }} flexShrink={0}>
          <Input
            value={value.state}
            onChange={(e) => onChange({ state: e.target.value.toUpperCase() })}
            isDisabled={isDisabled}
            isReadOnly={loadingZipCode}
            maxLength={2}
            autoComplete="address-level1"
          />
        </FormField>
      </Flex>
    </>
  )
}
